/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'
import {Box, Flex, useMultiStyleConfig, Link as ChakraLink} from '@chakra-ui/react'
import {ListMenuPopover} from './list-menu-popover'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useGetMainMenu} from '../../custom-api/hooks/use-get-main-menu'
import {getGlobalLink} from '../../utils/contentstack-utils'
import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'
import useCorporateLayout from '../../hooks/use-corporate-layout'

/**
 * This is the navigation component used for desktop devices. Holds the site navigation,
 * providing users with a way to access all product categories and other important pages.
 * The submenus are open when the user moves the mouse over the trigger and for A11y when
 * users use the keyboard Tab key to focus over the chevron icon and press Enter.
 */

const ListMenu = () => {
    const [ariaBusy, setAriaBusy] = useState(true)
    const [selectedMenuItem, setSelectedMenuItem] = useState(null)
    const intl = useIntl()
    const {data} = useGetMainMenu()

    const categoriesData = useGetCategoriesData()
    const styles = useMultiStyleConfig('ListMenu')
    const {showCorporateLayout} = useCorporateLayout()

    const handleCardClick = () => {
        setSelectedMenuItem(null)
    }

    useEffect(() => {
        setAriaBusy(false)
    }, [])

    return showCorporateLayout ? null : (
        <nav
            id="list-menu"
            aria-label={intl.formatMessage({
                id: 'list_menu.nav.assistive_msg',
                defaultMessage: 'Main navigation',
            })}
            aria-live="polite"
            aria-busy={ariaBusy}
            aria-atomic="true"
        >
            {data && categoriesData && (
                <Flex {...styles.container} onMouseLeave={() => setSelectedMenuItem(null)}>
                    {data.primarycategories.map((primarycategory) => {
                        if (primarycategory._content_type_uid === 'firstlevelsimplelink') {
                            const {url, label, isAbsolute} = getGlobalLink({
                                link: primarycategory.redirects,
                                categoriesData,
                            })
                            return (
                                <Box
                                    key={primarycategory.uid}
                                    onMouseEnter={() => setSelectedMenuItem(null)}
                                    {...styles.listMenuTriggerContainer}
                                    color={primarycategory?.label_hex_color}
                                >
                                    {isAbsolute ? (
                                        <ChakraLink
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            {...styles.listMenuSimpleTriggerLink}
                                            onClick={handleCardClick}
                                        >
                                            {label}
                                        </ChakraLink>
                                    ) : (
                                        <Link
                                            as={RouteLink}
                                            to={url}
                                            {...styles.listMenuSimpleTriggerLink}
                                            onClick={handleCardClick}
                                        >
                                            {label}
                                        </Link>
                                    )}
                                </Box>
                            )
                        }
                        return (
                            <ListMenuPopover
                                selectedMenuItem={selectedMenuItem}
                                setSelectedMenuItem={setSelectedMenuItem}
                                key={primarycategory.uid}
                                primarycategory={primarycategory}
                                categoriesData={categoriesData}
                                onItemClick={handleCardClick}
                            />
                        )
                    })}
                </Flex>
            )}
        </nav>
    )
}

ListMenu.displayName = 'ListMenu'

ListMenu.propTypes = {}

export {ListMenu}
